import { LinkProps, Link as MuiLink } from "@mui/material";
import useGetAccount from "api/queries/useGetAccount";
import { PropsWithChildren } from "react";
import { openUrlInNewTab } from "utils/openUrlInNewTab";
import { resolveExternalUrl } from "utils/resolveExternalUrl";
import { isExternalLink } from "./isExternalLink";

/**
 * Accepting only a small set of props for now.
 */
interface Props {
	href?: string;
	onClick?: LinkProps["onClick"];

	/**
	 * The target applied to internal links only
	 */
	internalTarget?: LinkProps["target"];

	/**
	 * @deprecated As we figure out our design system we can hopefully remove this.
	 */
	sx?: LinkProps["sx"];

	/**
	 * @deprecated As we figure out our design system we can hopefully remove this.
	 */
	variant?: LinkProps["variant"];
}

export const Link = ({
	internalTarget,
	...props
}: PropsWithChildren<Props>) => {
	if (isExternalLink(props.href)) {
		return <ExternalLink {...props} />;
	}

	return <MuiLink target={internalTarget} {...props} />;
};

const ExternalLink = (props: LinkProps) => {
	const { data: account } = useGetAccount();
	const href = resolveExternalUrl(props.href ?? "", account ?? undefined);

	const navigate = async () => {
		await openUrlInNewTab(props.href ?? "", account ?? undefined);
	};

	const handleClick: LinkProps["onClick"] = async (event) => {
		event.preventDefault();
		await navigate();
	};

	const handleAuxClick: LinkProps["onAuxClick"] = async (event) => {
		if (event.button === 1) {
			event.preventDefault();
			await navigate();
		}
	};

	const handleKeyDown: LinkProps["onKeyDown"] = async (event) => {
		if (event.key === "Enter") {
			event.preventDefault();
			await navigate();
		}
	};

	return (
		<MuiLink
			{...props}
			href={href}
			role="link"
			tabIndex={0}
			target="_blank"
			rel="noopener noreferrer nofollow"
			onClick={handleClick}
			onAuxClick={handleAuxClick}
			onKeyDown={handleKeyDown}
		/>
	);
};
